import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Table } from "react-bootstrap";

import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import { STRIPE_PUBLISHABLE_KEY } from "../../utils/env";
import { formatMoney } from "../../utils/helper";

const stripePromiseCache = {};

const getStripePromise = (key) => {
  if (!key) return null;
  if (!stripePromiseCache[key]) {
    stripePromiseCache[key] = loadStripe(key);
  }
  return stripePromiseCache[key];
};

const PaymentForm = ({
  paymentInfo,
  bookingId,
  onPaid,
  onFailed,
  onCancel,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const buildReturnUrl = () => {
    const url = new URL(window.location.href);
    // Strip any stale Stripe return params before adding ours.
    url.searchParams.delete("payment_intent");
    url.searchParams.delete("payment_intent_client_secret");
    url.searchParams.delete("redirect_status");
    if (bookingId) {
      url.searchParams.set("paymentBookingId", bookingId);
    }
    return url.toString();
  };

  const handlePay = async () => {
    if (loading || !stripe || !elements) return;
    setLoading(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setLoading(false);
      if (submitError.type === "validation_error") {
        onFailed(submitError.message, false);
      } else {
        onFailed(submitError.message, true);
      }
      return;
    }

    const { paymentIntent, error } = await stripe.confirmPayment({
      elements,
      clientSecret: paymentInfo.clientSecret,
      confirmParams: {
        return_url: buildReturnUrl(),
      },
      redirect: "if_required",
    });

    setLoading(false);

    if (error) {
      // validation_error / card_error are user-fixable, keep the form open;
      // anything else routes to the retry/cancel dialog.
      const recoverable =
        error.type === "validation_error" || error.type === "card_error";
      onFailed(error.message, !recoverable);
      return;
    }

    if (paymentIntent && paymentIntent.status === "succeeded") {
      onPaid();
    } else if (paymentIntent) {
      onFailed(`Payment ${paymentIntent.status}. Please try again.`, true);
    }
  };

  return (
    <div className="space-booking-infos">
      <div className="mb-4">
        <Table>
          <thead>
            <tr>
              <th>Description</th>
              <th className="text-right">Amount</th>
            </tr>
            <tr>
              <td>Space Cost</td>
              <td className="text-right">
                ${formatMoney(paymentInfo.grossAmount)}
              </td>
            </tr>
            <tr>
              <td>Stripe Platform Fees</td>
              <td className="text-right">
                ${formatMoney(paymentInfo.stripeFee)}
              </td>
            </tr>
            <tr>
              <td>
                <b>Total</b>
              </td>
              <td className="text-right">
                ${formatMoney(paymentInfo.amount)}
              </td>
            </tr>
          </thead>
        </Table>
      </div>

      <div className="stripe-payment-wrapper mb-3">
        <PaymentElement onReady={() => setReady(true)} />
      </div>

      <div className="payment-actions d-flex gap-2 mt-2">
        <Button
          variant=""
          className="btn-pill-outline flex-fill"
          disabled={loading}
          onClick={onCancel}
        >
          <span>Cancel</span>
        </Button>
        <Button
          variant=""
          className="btn-pill-primary flex-fill"
          disabled={loading || !ready || !stripe}
          onClick={handlePay}
        >
          <span>
            {loading && <i className="fas fa-spin fa-spinner me-2" />}
            {loading ? "Processing…" : "Pay Now"}
          </span>
        </Button>
      </div>
    </div>
  );
};

const StripePaymentElement = ({
  paymentInfo,
  bookingId,
  onPaid,
  onFailed,
  onCancel,
}) => {
  const stripeKey = useSelector((state) => state.auth && state.auth.stripeKey);
  const effectiveKey = stripeKey || STRIPE_PUBLISHABLE_KEY;
  const stripePromise = useMemo(
    () => getStripePromise(effectiveKey),
    [effectiveKey],
  );

  if (!paymentInfo || !paymentInfo.clientSecret) {
    return <p className="text-center text-muted my-4">Preparing payment…</p>;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: paymentInfo.clientSecret,
        appearance: { theme: "stripe" },
      }}
    >
      <PaymentForm
        paymentInfo={paymentInfo}
        bookingId={bookingId}
        onPaid={onPaid}
        onFailed={onFailed}
        onCancel={onCancel}
      />
    </Elements>
  );
};

export default StripePaymentElement;
